@let corporateDesign = corporateDesignService.corporateDesign;
@let footer = footerStore.footer();
@let footerBackground = corporateDesign().colorAdmixtures.dark_surface_01;
@let isDarkMode = corporateDesignService.isDarkModeActive();
@let isLoading =
  corporateDesignService.isCorporateDesignLoading() || footerStore.isLoading();
@if (isLoading) {
  <div class="pt-32">
    <aw-loading-animation
      [entityType]="EEntityType.footer"
      [relationType]="RelationType.loading"></aw-loading-animation>
  </div>
}
@if (corporateDesign() && footer && !isLoading) {
  <div
    class="flex w-full justify-center"
    [attr.data-testid]="'footer'"
    [style.color]="
      isDarkMode
        ? footer.appearance?.fontColorDark ||
          corporateDesignService.darkFontColor(
            corporateDesign().primaryFontColor
          )
        : footer.appearance?.fontColor || corporateDesign().primaryFontColor
    "
    [ngStyle]="{
      backgroundColor: isDarkMode
        ? footer.appearance?.backgroundColorDark || footerBackground
        : footer.appearance?.backgroundColor || footer.backgroundColor,
      backgroundImage: backgroundImage(footer),
      backgroundPosition: 'center',
      backgroundRepeat: 'no-repeat',
      backgroundSize: 'cover',
    }">
    <div class="w-full max-w-250">
      <div
        class="bottom-0 flex min-h-10 w-full max-w-full flex-wrap items-start justify-start gap-2 overflow-hidden p-2 text-left">
        <!-- Link Block -->
        @if (isFooterBlockVisible(footer, 'linkArea')) {
          <aw-footer-block
            [children]="linkBlockChildren"
            [style.order]="footerBlockOrder(footer, 'linkArea')"
            name="link"
            [showHeadline]="isHeadlineVisible(footer, 'linkArea')"
            headline="{{ footer.linkArea.headlines | language }}"
            [headlineSize]="corporateDesign().fontSizeTitle">
            <ng-template #linkBlockChildren>
              @for (link of footer.linkArea.links; track link.id) {
                @let url =
                  link.texts | filterTexts: RelationType.url | language;
                @if (url) {
                  <a
                    [attr.data-testid]="'link-' + link.id"
                    [style.font-size]="
                      corporateDesign().fontSizeText | toFontSize
                    "
                    class="hover:text-primary-main block hover:underline"
                    [href]="url"
                    [id]="link.id">
                    {{ link.texts | filterTexts: RelationType.text | language }}
                  </a>
                } @else {
                  <span
                    [style.font-size]="
                      corporateDesign().fontSizeText | toFontSize
                    "
                    >{{
                      link.texts | filterTexts: RelationType.text | language
                    }}</span
                  >
                }
              }
            </ng-template>
          </aw-footer-block>
        }

        @if (isFooterBlockVisible(footer, 'paymentArea')) {
          <!-- Payment Block -->
          <aw-footer-block
            [children]="paymentBlockChildren"
            [style.order]="footerBlockOrder(footer, 'paymentArea')"
            name="payment"
            [showHeadline]="isHeadlineVisible(footer, 'paymentArea')"
            headline="{{ footer.paymentArea.headlines | language }}"
            [headlineSize]="corporateDesign().fontSizeTitle">
            <ng-template #paymentBlockChildren>
              @for (
                paymentImage of footer.paymentArea.accessibleImages;
                track paymentImage.id
              ) {
                @if (paymentImage.image) {
                  @if (paymentImage.url) {
                    <a
                      [attr.data-testid]="'img-link-' + paymentImage.id"
                      class="my-3 inline-block rounded bg-white p-1"
                      [href]="paymentImage.url"
                      [id]="paymentImage.id"
                      ><img
                        [attr.data-testid]="'image-' + paymentImage.id"
                        [alt]="paymentImage.imageAlts | language"
                        [src]="
                          footerStore.requestPath(
                            paymentImage.id,
                            'paymentArea'
                          )
                        "
                        class="block h-auto max-h-8"
                    /></a>
                  } @else {
                    <span class="my-3 inline-block rounded bg-white p-1"
                      ><img
                        [attr.data-testid]="'image-' + paymentImage.id"
                        [alt]="paymentImage.imageAlts | language"
                        [src]="
                          footerStore.requestPath(
                            paymentImage.id,
                            'paymentArea'
                          )
                        "
                        class="block h-auto max-h-8"
                    /></span>
                  }
                }
              }
            </ng-template>
          </aw-footer-block>
        }

        @if (isFooterBlockVisible(footer, 'addressArea')) {
          <!-- Address Block -->
          <aw-footer-block
            [style.order]="footerBlockOrder(footer, 'addressArea')"
            name="address"
            [children]="addressBlockChildren"
            [showHeadline]="isHeadlineVisible(footer, 'addressArea')"
            headline="{{
              footer.addressArea.texts
                | filterTexts: RelationType.headline
                | language
            }}"
            [headlineSize]="corporateDesign().fontSizeTitle">
            <ng-template #addressBlockChildren>
              <quill-view
                [content]="footer.addressArea.texts | language"
                [attr.aria-label]="footer.addressArea.texts | language"
                format="json"
                theme="snow"></quill-view>
            </ng-template>
          </aw-footer-block>
        }

        @if (isFooterBlockVisible(footer, 'logoArea')) {
          <!-- Logo Block -->
          <aw-footer-block
            [children]="logoBlockChildren"
            [style.order]="footerBlockOrder(footer, 'logoArea')"
            name="logo"
            [showHeadline]="isHeadlineVisible(footer, 'logoArea')"
            headline="{{ footer.logoArea.headlines | language }}"
            [headlineSize]="corporateDesign().fontSizeTitle">
            <ng-template #logoBlockChildren>
              <div
                class="flex"
                [class.flex-col]="
                  (footer.logoArea.orientation ?? 'vertical') === 'vertical'
                "
                [class.flex-row]="footer.logoArea.orientation === 'horizontal'"
                [class.flex-wrap]="footer.logoArea.orientation === 'horizontal'"
                [class.gap-3]="true">
                @for (img of footer.logoArea.accessibleImages; track img.id) {
                  @if (img.image) {
                    @if (img.url) {
                      <a
                        [attr.data-testid]="'img-link-' + $index"
                        class="my-3 inline-block"
                        [href]="img.url"
                        [id]="img.id"
                        ><img
                          [attr.data-testid]="'image-' + $index"
                          [alt]="img.imageAlts | language"
                          [src]="footerStore.requestPath(img.id, 'logoArea')"
                          class="block h-auto max-h-8"
                      /></a>
                    } @else {
                      <span class="my-3 inline-block"
                        ><img
                          [attr.data-testid]="'image-' + $index"
                          [alt]="img.imageAlts | language"
                          [src]="footerStore.requestPath(img.id, 'logoArea')"
                          class="block h-auto max-h-8"
                      /></span>
                    }
                  }
                }
              </div>
            </ng-template>
          </aw-footer-block>
        }
        @for (block of footer.blocks ?? []; track block.id) {
          @if (
            block.isActive &&
            block.id &&
            block.positionOrder >= 4 &&
            isFooterBlockVisible(footer, block.id)
          ) {
            <aw-footer-block
              [block]="block"
              [style.order]="
                footerBlockOrder(footer, block.id)
              "></aw-footer-block>
          }
        }
        
        @if (hasRowBreak(footer)) {
          <div
            class="w-full"
            [style.order]="footerBlockOrder(footer, 'ROW_BREAK')"></div>
        }
      </div>
    </div>
  </div>
}
